import { Layout } from "@/components/layout";
import {
  useGetSalesStatus,
  useListSaleEmails,
  useGetSaleEmail,
  useSyncSales,
  useRematchSaleEmail,
  useResyncSaleEmails,
  useUnlinkSaleEmail,
  useManualMatchSaleEmail,
  useReviewSaleEmail,
  useIgnoreSaleEmail,
  useGetMe,
  getGetSalesStatusQueryKey,
  getListSaleEmailsQueryKey,
  getGetSaleEmailQueryKey,
  type SaleEmailListItem,
  type SaleCandidate,
  type ListSaleEmailsStatus,
} from "@workspace/api-client-react";
import {
  Loader2,
  RefreshCw,
  CheckCircle2,
  Link2,
  Unlink2,
  EyeOff,
  Eye,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import ReactMarkdown from "react-markdown";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { RematchDialog } from "@/components/rematch-dialog";

type FilterKey =
  | "all"
  | "unmatched"
  | "matched"
  | "existing_reach_sale"
  | "partial"
  | "sold_out"
  | "ambiguous"
  | "quantity_conflict"
  | "parse_error"
  | "needs_review"
  | "ignored"
  // UI-only aggregate bucket surfaced by the summary strip. There is no
  // server-side filter for it, so we request "all" and filter client-side
  // to the three error-derived statuses below.
  | "errors";

// derivedStatus values that make up the "Errors" summary bucket. Kept in sync
// with the aggregated count (parse_error + quantity_conflict + ambiguous).
const ERROR_STATUSES = new Set(["parse_error", "quantity_conflict", "ambiguous"]);

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "needs_review", label: "Needs Review" },
  { key: "unmatched", label: "Unmatched" },
  { key: "matched", label: "Matched" },
  { key: "partial", label: "Partial Sale" },
  { key: "sold_out", label: "Sold Out" },
  { key: "existing_reach_sale", label: "Existing Reach Sale" },
  { key: "ambiguous", label: "Ambiguous" },
  { key: "quantity_conflict", label: "Qty Conflict" },
  { key: "parse_error", label: "Parse Error" },
  { key: "ignored", label: "Ignored" },
];

// The four Ticketmaster lifecycle stages, in order, plus an "All" bucket. Each
// stage maps to one or more underlying email_type discriminators.
type TypeKey = "all" | "listing" | "sale" | "delivery" | "payment";

const TYPE_FILTERS: { key: TypeKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "listing", label: "Listing" },
  { key: "sale", label: "Sale" },
  { key: "delivery", label: "Delivery" },
  { key: "payment", label: "Payment" },
];

// Map each lifecycle stage to the email_type values that belong to it. "Sale"
// covers both the "just sold" notification and the later sale confirmation.
const TYPE_MEMBERS: Record<Exclude<TypeKey, "all">, Set<string>> = {
  listing: new Set(["listing_confirmation"]),
  sale: new Set(["sale_notification", "sale_confirmation"]),
  delivery: new Set(["delivery"]),
  payment: new Set(["payment"]),
};

function typeMatches(typeKey: TypeKey, item: { emailType?: string }): boolean {
  if (typeKey === "all") return true;
  return TYPE_MEMBERS[typeKey].has(item.emailType ?? "");
}

// Lower-case labels for the empty-state copy (e.g. "No delivery emails yet.").
const TYPE_LABEL_LOWER: Record<Exclude<TypeKey, "all">, string> = {
  listing: "listing",
  sale: "sale",
  delivery: "delivery",
  payment: "payment",
};

// Human-readable label for a raw email_type, used in the detail drawer.
const EMAIL_TYPE_LABEL: Record<string, string> = {
  listing_confirmation: "Listing",
  sale_notification: "Sale (notification)",
  sale_confirmation: "Sale (confirmation)",
  delivery: "Delivery",
  payment: "Payment",
  unknown: "Unknown",
};

const TYPE_STORAGE_KEY = "tex.emails.type";

function readStoredType(): TypeKey {
  if (typeof window === "undefined") return "all";
  const v = window.localStorage.getItem(TYPE_STORAGE_KEY);
  if (
    v === "all" ||
    v === "listing" ||
    v === "sale" ||
    v === "delivery" ||
    v === "payment"
  ) {
    return v;
  }
  return "all";
}

// Client-side mirror of the server's status filter (see GET /emails). The
// "errors" key is a UI-only aggregate with no server equivalent.
function itemMatchesFilter(
  filter: FilterKey,
  item: SaleEmailListItem,
): boolean {
  const m = item.match;
  const reviewed = Boolean(m?.reviewedAt);
  const ignored = Boolean(item.ignoredAt);
  if (filter === "ignored") return ignored;
  if (ignored) return false;
  switch (filter) {
    case "all":
      return true;
    case "errors":
      return ERROR_STATUSES.has(item.derivedStatus);
    case "parse_error":
      return item.parseStatus === "parse_error";
    case "unmatched":
      return !m || m.matchStatus === "no_match";
    case "matched":
      return m?.matchStatus === "matched" || m?.matchStatus === "partial";
    case "partial":
      return m?.matchStatus === "partial";
    case "sold_out":
      return (
        m?.matchStatus === "matched" &&
        m.quantityRemaining != null &&
        m.quantityRemaining <= 0
      );
    case "ambiguous":
      return m?.matchStatus === "ambiguous";
    case "quantity_conflict":
      return m?.matchStatus === "quantity_conflict";
    case "existing_reach_sale":
      return item.hasReachInvoice;
    case "needs_review":
      return (
        !reviewed &&
        (item.parseStatus === "parse_error" ||
          m?.matchStatus === "ambiguous" ||
          m?.matchStatus === "quantity_conflict" ||
          m?.matchStatus === "no_match" ||
          !m)
      );
    default:
      return true;
  }
}

const STATUS_LABELS: Record<string, string> = {
  matched: "Matched",
  partial: "Partial",
  sold_out: "Sold Out",
  ambiguous: "Ambiguous",
  no_match: "Unmatched",
  quantity_conflict: "Qty Conflict",
  parse_error: "Parse Error",
  pending: "Pending",
  listed: "Listed",
};

function statusVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "matched":
    case "sold_out":
      return "default";
    case "partial":
    case "listed":
      return "secondary";
    case "ambiguous":
    case "quantity_conflict":
    case "parse_error":
      return "destructive";
    default:
      return "outline";
  }
}

// Map a raw email_type to its lifecycle stage so the list/detail can colour-code
// each email Gmail-style (a coloured left border + avatar tint per stage).
type Stage = Exclude<TypeKey, "all"> | "other";

function stageOf(emailType?: string | null): Stage {
  if (!emailType) return "other";
  for (const k of ["listing", "sale", "delivery", "payment"] as const) {
    if (TYPE_MEMBERS[k].has(emailType)) return k;
  }
  return "other";
}

// Per-stage accent classes. `border` is the 3px left rail, `avatar` tints the
// initial bubble. Keyed: listing=blue, sale=green, delivery=purple, payment=amber.
const STAGE_ACCENT: Record<Stage, { border: string; avatar: string }> = {
  listing: { border: "border-l-blue-500", avatar: "bg-blue-500/15 text-blue-400" },
  sale: { border: "border-l-green-500", avatar: "bg-green-500/15 text-green-400" },
  delivery: {
    border: "border-l-purple-500",
    avatar: "bg-purple-500/15 text-purple-400",
  },
  payment: {
    border: "border-l-amber-500",
    avatar: "bg-amber-500/15 text-amber-400",
  },
  other: {
    border: "border-l-muted-foreground/30",
    avatar: "bg-muted text-muted-foreground",
  },
};

// Left-hand status dot colour, derived from the match-derived status so the
// operator can triage a row without reading the badge.
function statusDotClass(status: string): string {
  switch (status) {
    case "matched":
    case "sold_out":
      return "bg-green-500";
    case "partial":
      return "bg-amber-500";
    case "ambiguous":
    case "quantity_conflict":
    case "parse_error":
      return "bg-red-500";
    default:
      return "bg-muted-foreground/40";
  }
}

// First letter for the avatar bubble — prefers the recipient/account, falls back
// to the event name.
function initialOf(...candidates: (string | null | undefined)[]): string {
  for (const c of candidates) {
    const t = c?.trim();
    if (t) return t[0].toUpperCase();
  }
  return "?";
}

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const MONTH_INDEX: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

interface EventDateParts {
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
}

// Pull date/time components out of a free-form Ticketmaster date string. Handles
// both US month-first ("June 17, 2026 7:00 PM" / "Fri • May 02, 2025 • 7:30 PM")
// and EU day-first ("Wednesday 17 June 2026 17:00" / "17 June 2026") variants,
// plus an optional weekday prefix and 12h or 24h time. Returns null when no
// recognisable month + day can be found.
function parseEventDateParts(raw: string): EventDateParts | null {
  const m = raw.match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*/i);
  if (!m || m.index == null) return null;
  const month = MONTH_INDEX[m[1].toLowerCase()];

  let day: number | undefined;
  const after = raw.slice(m.index + m[0].length).match(/^\s*,?\s*(\d{1,2})\b/);
  if (after) {
    day = Number(after[1]);
  } else {
    const before = raw.slice(0, m.index).match(/(\d{1,2})\s*$/);
    if (before) day = Number(before[1]);
  }
  if (day == null || day < 1 || day > 31) return null;

  const yearMatch = raw.match(/\b(\d{4})\b/);
  const year = yearMatch ? Number(yearMatch[1]) : new Date().getFullYear();

  let hour: number | undefined;
  let minute: number | undefined;
  const t12 = raw.match(/\b(\d{1,2}):(\d{2})\s*(am|pm)\b/i);
  const t24 = raw.match(/\b(\d{1,2}):(\d{2})\b/);
  if (t12) {
    hour = Number(t12[1]) % 12;
    if (/pm/i.test(t12[3])) hour += 12;
    minute = Number(t12[2]);
  } else if (t24) {
    hour = Number(t24[1]);
    minute = Number(t24[2]);
  }

  return { year, month, day, hour, minute };
}

// Normalise any event-date string (email `eventDateText` OR inventory ISO
// `eventDate`) into a clean, consistent label like "Jun 17, 2026 · 7:00 PM",
// dropping the time portion when none is present. Falls back to the raw string
// when nothing parseable is found so we never hide data.
function fmtEventDate(text: string | null | undefined): string {
  if (!text) return "—";
  const raw = String(text).trim();
  if (!raw) return "—";

  // ISO-ish inventory date ("2026-06-12" / "2026-06-12T19:00:00Z").
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  let parts: EventDateParts | null = null;
  if (iso) {
    parts = {
      year: Number(iso[1]),
      month: Number(iso[2]) - 1,
      day: Number(iso[3]),
      hour: iso[4] != null ? Number(iso[4]) : undefined,
      minute: iso[5] != null ? Number(iso[5]) : undefined,
    };
  } else {
    parts = parseEventDateParts(raw);
  }
  if (!parts) return raw;

  const d = new Date(
    parts.year,
    parts.month,
    parts.day,
    parts.hour ?? 0,
    parts.minute ?? 0,
  );
  if (Number.isNaN(d.getTime())) return raw;

  const datePart = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  if (parts.hour == null) return datePart;
  const timePart = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart} · ${timePart}`;
}

// Human-readable byte count (e.g. 3_145_728 -> "3 MB"). Used to show operators
// how much data the last sync's discovery query scanned in BigQuery.
function fmtBytes(n: number | null | undefined): string | null {
  if (n == null) return null;
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const rounded = value >= 100 || Number.isInteger(value) ? Math.round(value) : Number(value.toFixed(1));
  return `${rounded} ${units[i]}`;
}

export default function Emails() {
  const [typeKey, setTypeKeyState] = useState<TypeKey>(readStoredType);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [backfillFrom, setBackfillFrom] = useState("");
  const qc = useQueryClient();

  // Switching the lifecycle type resets the status filter so a carried-over
  // filter never lands us on an empty state, and persists the choice across
  // visits.
  const changeType = (next: TypeKey) => {
    setTypeKeyState(next);
    if (typeof window !== "undefined")
      window.localStorage.setItem(TYPE_STORAGE_KEY, next);
    setFilter("all");
  };

  const { data: status } = useGetSalesStatus();
  // All grouping/filtering is client-side, so we fetch the full set once. The
  // server hides ignored emails from every status except "ignored", so we pull
  // that bucket separately and merge — giving a complete list to compute counts
  // from and drive the table.
  const active = useListSaleEmails({
    status: "all" as ListSaleEmailsStatus,
    search: search || undefined,
  });
  const ignoredList = useListSaleEmails({
    status: "ignored" as ListSaleEmailsStatus,
    search: search || undefined,
  });

  const isLoading = active.isLoading || ignoredList.isLoading;
  const isFetching = active.isFetching || ignoredList.isFetching;

  // Full merged set across all statuses (incl. ignored). Drives the per-type
  // count badges, which stay independent of the active type so each badge shows
  // that stage's total.
  const allItems = useMemo(() => {
    return [
      ...(active.data?.items ?? []),
      ...(ignoredList.data?.items ?? []),
    ];
  }, [active.data, ignoredList.data]);

  // Items for the active lifecycle type, across all statuses. Drives the status
  // toggle badges, summary strip, and (after the status filter) the table.
  const typeItems = useMemo(() => {
    return allItems.filter((i) => typeMatches(typeKey, i));
  }, [allItems, typeKey]);

  const items = useMemo(() => {
    return typeItems.filter((i) => itemMatchesFilter(filter, i));
  }, [typeItems, filter]);

  // Per-type totals for the type-filter badges (all statuses).
  const typeCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const t of TYPE_FILTERS) {
      c[t.key] = allItems.filter((i) => typeMatches(t.key, i)).length;
    }
    return c;
  }, [allItems]);

  // Status-toggle badges + summary strip, scoped to the active type so the
  // numbers always match what the current view can show.
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const f of FILTERS) {
      c[f.key] = typeItems.filter((i) => itemMatchesFilter(f.key, i)).length;
    }
    return c;
  }, [typeItems]);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: getGetSalesStatusQueryKey() });
    qc.invalidateQueries({ queryKey: getListSaleEmailsQueryKey() });
  };

  const sync = useSyncSales({
    mutation: {
      onSuccess: (s) => {
        toast.success(
          `Sync complete: ${s.inserted} new, ${s.matched + s.partial} matched, ${s.parseErrors} parse errors`,
        );
        invalidateAll();
      },
      onError: () => toast.error("Sync failed — check BigQuery configuration"),
    },
  });

  const { data: me } = useGetMe();
  const canResync = me?.role === "admin" || me?.role === "operator";

  const resync = useResyncSaleEmails({
    mutation: {
      onSuccess: (s) => {
        toast.success(
          `Resync complete: ${s.totalProcessed} processed, ${s.reparsed} re-parsed, ${s.changed} changed, ${s.errors} errors`,
        );
        invalidateAll();
      },
      onError: () => toast.error("Resync failed"),
    },
  });

  const rowRematch = useRematchSaleEmail({
    mutation: {
      onSuccess: () => {
        toast.success("Re-ran");
        invalidateAll();
      },
      onError: () => toast.error("Re-run failed"),
    },
  });

  const errorCount = typeItems.filter((i) =>
    itemMatchesFilter("errors", i),
  ).length;

  // Which optional columns the current lifecycle type shows. "All" keeps only
  // the common subset.
  const showSecRowSeats = typeKey === "listing" || typeKey === "sale";
  const showQty = typeKey === "sale";
  const showPayout = typeKey === "sale" || typeKey === "payment";
  const showListedPrice = typeKey === "listing";

  const notConfigured = status && (!status.configured || !status.reachConfigured);

  // Surface a failed background sync only when the failure is more recent than
  // the last success — otherwise a stale error would nag after things recover.
  const syncError = status?.lastSyncError ?? null;
  const showSyncError =
    syncError != null &&
    (!status?.lastSyncAt ||
      new Date(syncError.at).getTime() > new Date(status.lastSyncAt).getTime());
  const syncErrorIsQuota =
    syncError != null && /quota/i.test(syncError.message);

  return (
    <Layout>
      <div className="flex flex-col h-full w-full bg-background p-2 sm:p-4 overflow-hidden">
        <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shrink-0">
          <div>
            <h1 className="text-lg font-medium text-foreground">Emails</h1>
            <p className="text-xs text-muted-foreground">
              {status?.lastSyncAt
                ? `Last synced ${fmtDate(status.lastSyncAt)}`
                : `Backfill from ${status?.defaultBackfillFrom ?? "—"}`}
            </p>
            {status?.lastSyncSummary && (
              <p
                className="text-xs text-muted-foreground"
                title="How much data BigQuery scanned for the last sync's discovery query, and the point in time it started reading from."
              >
                {(() => {
                  const summary = status.lastSyncSummary;
                  const scanned = fmtBytes(summary.bytesProcessed);
                  const from = fmtDate(summary.dateFrom);
                  if (scanned) {
                    return `Last sync scanned ${scanned} from ${from}`;
                  }
                  return `Last sync read from ${from}`;
                })()}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <div className="w-full sm:w-72">
              <Input
                placeholder="Search listing #, recipient, event, section…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="whitespace-nowrap">Backfill from</span>
              <Input
                type="date"
                value={backfillFrom}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setBackfillFrom(e.target.value)}
                placeholder="incremental"
                className="h-8 w-[140px]"
                title="Leave blank for a quick incremental catch-up (only mail newer than the last email already stored). Pick a date to deliberately backfill from that date."
              />
            </label>
            <Button
              size="sm"
              className="h-8 whitespace-nowrap"
              onClick={() =>
                sync.mutate({
                  data: backfillFrom ? { dateFrom: backfillFrom } : {},
                })
              }
              disabled={sync.isPending || !status?.configured}
            >
              {sync.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              <span className="ml-1">Sync</span>
            </Button>
            {canResync && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 whitespace-nowrap rounded-full"
                onClick={() =>
                  resync.mutate({
                    data: {
                      mode: "both",
                      ...(backfillFrom ? { dateFrom: backfillFrom } : {}),
                    },
                  })
                }
                disabled={resync.isPending}
                title="Re-parse and re-match every stored email (no new fetch from BigQuery)."
              >
                {resync.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 text-blue-400" />
                )}
                <span className="ml-1">Resync all</span>
              </Button>
            )}
            {canResync && (
              <RematchDialog
                disabled={resync.isPending || sync.isPending}
                onApplied={invalidateAll}
              />
            )}
          </div>
        </div>

        {showSyncError && syncError && (
          <div className="mb-3 shrink-0 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <span className="font-medium">Last sync failed</span>
            {" — "}
            {syncErrorIsQuota
              ? "BigQuery's daily query quota is exhausted, so new emails can't be pulled until it resets. They'll appear automatically once quota is available."
              : syncError.message}
            <span className="text-destructive/70">
              {" "}
              ({fmtDate(syncError.at)})
            </span>
          </div>
        )}

        {notConfigured && (
          <div className="mb-3 shrink-0 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-400">
            {!status?.configured && (
              <span>BigQuery is not configured — syncing is disabled.</span>
            )}
            {!status?.configured && !status?.reachConfigured && <span> </span>}
            {!status?.reachConfigured && (
              <span>
                Reach is not configured — existing-sale lookups are unavailable.
              </span>
            )}
          </div>
        )}

        <div className="mb-3 flex flex-wrap items-center gap-2 shrink-0">
          <ToggleGroup
            type="single"
            size="sm"
            value={typeKey}
            onValueChange={(v) => v && changeType(v as TypeKey)}
            className="flex-wrap justify-start gap-1"
          >
            {TYPE_FILTERS.map((t) => {
              const c = typeCounts[t.key];
              return (
                <ToggleGroupItem
                  key={t.key}
                  value={t.key}
                  className="h-7 px-2 text-xs"
                >
                  {t.label}
                  {c != null && c > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary/20 text-primary text-[10px] font-mono font-semibold leading-none">
                      {c}
                    </span>
                  )}
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>

          <span className="self-stretch border-l border-border" aria-hidden="true" />

          <ToggleGroup
            type="single"
            size="sm"
            value={filter}
            onValueChange={(v) => v && setFilter(v as FilterKey)}
            className="flex-wrap justify-start gap-1"
          >
            {FILTERS.map((f) => {
              const c = counts[f.key];
              return (
                <ToggleGroupItem
                  key={f.key}
                  value={f.key}
                  className="h-7 px-2 text-xs"
                >
                  {f.label}
                  {c != null && c > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary/20 text-primary text-[10px] font-mono font-semibold leading-none">
                      {c}
                    </span>
                  )}
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </div>

        <div className="mb-2 flex flex-wrap items-center gap-4 shrink-0 rounded-md border border-border bg-card px-3 py-1.5 text-xs">
          <button
            type="button"
            onClick={() => setFilter("matched")}
            className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
          >
            <span className="h-2 w-2 rounded-full ring-2 bg-green-500 ring-green-500/30" />
            <span className="font-mono text-foreground">{counts.matched ?? 0}</span>
            <span className="text-muted-foreground">Matched</span>
          </button>
          <span className="text-border">·</span>
          <button
            type="button"
            onClick={() => setFilter("needs_review")}
            className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
          >
            <span className="h-2 w-2 rounded-full ring-2 bg-amber-500 ring-amber-500/30" />
            <span className="font-mono text-foreground">
              {counts.needs_review ?? 0}
            </span>
            <span className="text-muted-foreground">Needs Review</span>
          </button>
          <span className="text-border">·</span>
          <button
            type="button"
            onClick={() => setFilter("unmatched")}
            className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
          >
            <span className="h-2 w-2 rounded-full ring-2 bg-muted-foreground/60 ring-muted-foreground/20" />
            <span className="font-mono text-foreground">
              {counts.unmatched ?? 0}
            </span>
            <span className="text-muted-foreground">Unmatched</span>
          </button>
          <span className="text-border">·</span>
          <button
            type="button"
            onClick={() => setFilter("errors")}
            className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
          >
            <span className="h-2 w-2 rounded-full ring-2 bg-red-500 ring-red-500/30" />
            <span className="font-mono text-foreground">{errorCount}</span>
            <span className="text-muted-foreground">Errors</span>
          </button>
        </div>

        <div className="flex-1 border border-border rounded-md bg-card overflow-hidden flex flex-col">
          <div className="flex-1 overflow-auto custom-scrollbar">
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
                No{typeKey === "all" ? "" : ` ${TYPE_LABEL_LOWER[typeKey]}`}{" "}
                emails {filter !== "all" ? "in this filter" : "yet"}.
              </div>
            ) : (
              <ul className="divide-y divide-border/50">
                {items.map((item: SaleEmailListItem) => {
                  const accent = STAGE_ACCENT[stageOf(item.emailType)];
                  const selected = selectedId === item.id;
                  const accountMismatch =
                    item.match?.inventoryId != null &&
                    item.match.vendorAccountEmail != null &&
                    item.recipientEmail != null &&
                    item.recipientEmail.toLowerCase() !==
                      item.match.vendorAccountEmail.toLowerCase();
                  // Compact metadata chips shown under the subject, scoped to the
                  // active lifecycle type so the row stays scannable.
                  const meta: React.ReactNode[] = [];
                  if (item.listingNumber)
                    meta.push(
                      <span key="ln" className="font-mono">
                        #{item.listingNumber}
                      </span>,
                    );
                  if (showSecRowSeats) {
                    const sr = [item.section, item.row]
                      .filter(Boolean)
                      .join(" / ");
                    if (sr || item.seats.length > 0)
                      meta.push(
                        <span key="sr">
                          {sr || "—"}
                          {item.seats.length > 0 &&
                            ` · ${item.seats.slice(0, 6).join(", ")}`}
                        </span>,
                      );
                  }
                  if (showQty && item.quantity != null)
                    meta.push(<span key="qty">Qty {item.quantity}</span>);
                  if (showPayout && item.totalPayout != null)
                    meta.push(
                      <span key="po">{fmtMoney(item.totalPayout)}</span>,
                    );
                  if (showListedPrice && item.listedPrice != null)
                    meta.push(
                      <span key="lp">{fmtMoney(item.listedPrice)}</span>,
                    );
                  return (
                    <li key={item.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedId(item.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedId(item.id);
                          }
                        }}
                        className={`group flex gap-3 border-l-[3px] px-4 py-3 cursor-pointer transition-colors ${
                          accent.border
                        } ${
                          selected
                            ? "bg-primary/10 hover:bg-primary/15"
                            : "hover:bg-muted/50"
                        }`}
                      >
                        <div className="flex items-center gap-2.5 shrink-0">
                          <span
                            className={`h-2 w-2 rounded-full ${statusDotClass(item.derivedStatus)}`}
                            title={
                              STATUS_LABELS[item.derivedStatus] ??
                              item.derivedStatus
                            }
                          />
                          <span
                            className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${accent.avatar}`}
                          >
                            {initialOf(item.recipientEmail, item.eventName)}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                              {item.eventName ?? "Unknown event"}
                            </span>
                            <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                              {fmtDate(item.receivedAt)}
                            </span>
                          </div>
                          <div className="mt-0.5 flex items-center gap-2">
                            <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
                              {item.emailSubject ??
                                (item.eventDateText
                                  ? fmtEventDate(item.eventDateText)
                                  : (item.recipientEmail ?? "—"))}
                            </span>
                            <div className="flex shrink-0 items-center gap-1">
                              {item.hasReachInvoice && (
                                <span title="Existing Reach invoice">
                                  <Link2 className="h-3.5 w-3.5 text-primary" />
                                </span>
                              )}
                              {item.match?.reviewedAt && (
                                <span title="Reviewed">
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                                </span>
                              )}
                              <Badge
                                variant={statusVariant(item.derivedStatus)}
                                className="text-[11px]"
                              >
                                {STATUS_LABELS[item.derivedStatus] ??
                                  item.derivedStatus}
                              </Badge>
                              {canResync && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 focus:opacity-100"
                                  title="Re-parse and re-match this email"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    rowRematch.mutate({
                                      id: item.id,
                                      data: { mode: "both" },
                                    });
                                  }}
                                  disabled={
                                    rowRematch.isPending &&
                                    rowRematch.variables?.id === item.id
                                  }
                                >
                                  {rowRematch.isPending &&
                                  rowRematch.variables?.id === item.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                          {meta.length > 0 && (
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground [&>span]:after:content-['·'] [&>span]:after:ml-2 [&>span]:after:text-border last:[&>span]:after:content-['']">
                              {meta}
                            </div>
                          )}
                          {accountMismatch ? (
                            <div
                              className="mt-1 truncate text-xs text-amber-500"
                              title={`Matched to ${item.match?.vendorAccountEmail}`}
                            >
                              → {item.match?.vendorAccountEmail}
                            </div>
                          ) : (
                            item.match?.inventoryId != null &&
                            item.match.vendorAccountEmail && (
                              <div
                                className="mt-1 truncate text-xs text-muted-foreground"
                                title={`Matched to ${item.match.vendorAccountEmail}`}
                              >
                                → {item.match.vendorAccountEmail}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {isFetching && !isLoading && (
              <div className="py-2 text-center">
                <Loader2 className="h-4 w-4 animate-spin text-primary inline" />
              </div>
            )}
          </div>
        </div>
      </div>

      <SaleDetailDrawer
        id={selectedId}
        onClose={() => setSelectedId(null)}
        onChanged={invalidateAll}
      />
    </Layout>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{value ?? "—"}</dd>
    </div>
  );
}

// Structured Section / Row / Seat display: section + row on the first line, the
// seat list on a second line, with overflow seats muted so long lists stay
// scannable.
function SeatBlock({
  section,
  row,
  seats,
}: {
  section?: string | null;
  row?: string | null;
  seats?: string[] | null;
}) {
  const seatList = seats ?? [];
  const MAX = 8;
  const shown = seatList.slice(0, MAX);
  const rest = seatList.slice(MAX);
  const headline =
    [section ? `Sec ${section}` : null, row ? `Row ${row}` : null]
      .filter(Boolean)
      .join(" · ") || "—";
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-sm font-medium text-foreground">{headline}</span>
      {seatList.length > 0 ? (
        <span className="text-sm text-foreground">
          {shown.join(", ")}
          {rest.length > 0 && (
            <span className="text-muted-foreground">
              {" "}
              +{rest.length} more
            </span>
          )}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">No seats</span>
      )}
    </div>
  );
}

// One labelled "From email" vs "Candidate listing" comparison row. Highlights
// the candidate cell amber when its value differs from the email so operators
// spot mismatches at a glance.
function CompareRow({
  label,
  emailValue,
  candidateValue,
  diff,
}: {
  label: string;
  emailValue: React.ReactNode;
  candidateValue: React.ReactNode;
  diff?: boolean;
}) {
  return (
    <div className="grid grid-cols-[88px_1fr_1fr] gap-2 py-1 text-sm border-b border-border/40 last:border-0">
      <span className="text-[10px] uppercase tracking-wide text-secondary-foreground self-center">
        {label}
      </span>
      <span className="text-foreground break-words">{emailValue ?? "—"}</span>
      <span
        className={`break-words ${diff ? "text-amber-500 font-medium" : "text-foreground"}`}
      >
        {candidateValue ?? "—"}
      </span>
    </div>
  );
}

function norm(v: unknown): string {
  return v == null ? "" : String(v).trim().toLowerCase();
}

function SaleDetailDrawer({
  id,
  onClose,
  onChanged,
}: {
  id: number | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const { data: detail, isLoading } = useGetSaleEmail(id ?? 0, {
    query: {
      enabled: id != null,
      queryKey: getGetSaleEmailQueryKey(id ?? 0),
    },
  });

  const refresh = () => {
    if (id != null)
      qc.invalidateQueries({ queryKey: getGetSaleEmailQueryKey(id) });
    onChanged();
  };

  const rematch = useRematchSaleEmail({
    mutation: {
      onSuccess: () => {
        toast.success("Rematched");
        refresh();
      },
      onError: () => toast.error("Rematch failed"),
    },
  });
  const unlink = useUnlinkSaleEmail({
    mutation: {
      onSuccess: () => {
        toast.success("Unlinked");
        refresh();
      },
      onError: () => toast.error("Unlink failed"),
    },
  });
  const manualMatch = useManualMatchSaleEmail({
    mutation: {
      onSuccess: () => {
        toast.success("Linked to inventory");
        refresh();
      },
      onError: () => toast.error("Manual match failed"),
    },
  });
  const review = useReviewSaleEmail({
    mutation: {
      onSuccess: () => {
        toast.success("Marked reviewed");
        refresh();
      },
      onError: () => toast.error("Review failed"),
    },
  });
  const ignore = useIgnoreSaleEmail({
    mutation: {
      onSuccess: () => {
        toast.success("Updated");
        refresh();
      },
      onError: () => toast.error("Ignore failed"),
    },
  });
  const [ignoreReason, setIgnoreReason] = useState("");

  const email = detail?.email;
  const match = detail?.match;
  const invoice = detail?.reachInvoice;
  const isIgnored = Boolean(email?.ignoredAt);

  return (
    <Sheet open={id != null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        {isLoading || !detail ? (
          <>
            <SheetHeader className="sr-only">
              <SheetTitle>Loading email</SheetTitle>
              <SheetDescription>Loading email details</SheetDescription>
            </SheetHeader>
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          </>
        ) : (
          <>
            <SheetHeader className="space-y-3 border-b border-border pb-4">
              <div className="flex items-start gap-3">
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-semibold ${
                    STAGE_ACCENT[stageOf(email?.emailType)].avatar
                  }`}
                >
                  {initialOf(email?.recipientEmail, email?.eventName)}
                </span>
                <div className="min-w-0 flex-1">
                  <SheetTitle className="truncate text-base font-semibold">
                    {email?.eventName ?? "Sale email"}
                  </SheetTitle>
                  <SheetDescription className="truncate text-sm">
                    {email?.emailSubject ?? "No subject"}
                  </SheetDescription>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {email?.emailType && (
                      <Badge variant="outline" className="text-[11px]">
                        {EMAIL_TYPE_LABEL[email.emailType] ?? email.emailType}
                      </Badge>
                    )}
                    <Badge variant={statusVariant(detail.derivedStatus)} className="text-[11px]">
                      {STATUS_LABELS[detail.derivedStatus] ??
                        detail.derivedStatus}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-[64px_1fr] gap-x-3 gap-y-1.5 text-sm">
                <span className="text-muted-foreground">From</span>
                <span className="text-foreground">Ticketmaster</span>
                <span className="text-muted-foreground">To</span>
                {email?.recipientEmail ? (
                  <Link
                    href={`/accounts?q=${encodeURIComponent(email.recipientEmail)}`}
                    className="truncate text-primary hover:underline"
                    onClick={onClose}
                  >
                    {email.recipientEmail}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">
                    unknown recipient
                  </span>
                )}
                <span className="text-muted-foreground">Date</span>
                <span className="text-foreground">
                  {fmtDate(email?.receivedAt)}
                </span>
                <span className="text-muted-foreground">Listing</span>
                <span className="font-mono text-foreground">
                  #{email?.listingNumber ?? "—"}
                </span>
              </div>
            </SheetHeader>

            <div className="mt-4 space-y-5">
              {detail.rawBody && (
                <div className="rounded-md border border-border bg-card p-4">
                  <div className="prose prose-sm prose-invert max-w-none leading-relaxed prose-headings:text-foreground prose-p:my-2 prose-a:text-primary prose-strong:text-foreground prose-li:my-0.5">
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a
                            {...props}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    >
                      {detail.rawBody}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
              {(() => {
                const recipient = email?.recipientEmail ?? null;
                const account = match?.vendorAccountEmail ?? null;
                const mismatch =
                  recipient != null &&
                  account != null &&
                  norm(recipient) !== norm(account);
                if (!mismatch) return null;
                return (
                  <div className="rounded-md border border-amber-500/50 bg-amber-500/5 p-3 text-sm">
                    <p className="text-amber-500">
                      Recipient (<span className="font-medium">{recipient}</span>
                      ) and matched account (
                      <span className="font-medium">{account}</span>) differ —
                      verify this is the right listing.
                    </p>
                  </div>
                );
              })()}

              <details open className="rounded-md border border-border bg-card">
                <summary className="flex cursor-pointer items-center justify-between px-4 py-2.5 text-sm font-semibold text-foreground">
                  Parsed details
                </summary>
                <div className="border-t border-border px-4 py-3">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                    <Field
                      label="Event date"
                      value={fmtEventDate(email?.eventDateText)}
                    />
                    <Field label="Venue" value={email?.venue} />
                    <div className="col-span-2">
                      <span className="text-xs font-medium text-muted-foreground">
                        Section / Row / Seats
                      </span>
                      <div className="mt-1">
                        <SeatBlock
                          section={email?.section}
                          row={email?.row}
                          seats={email?.seats}
                        />
                      </div>
                    </div>
                    <Field label="Quantity" value={email?.quantity} />
                    <Field
                      label="Listed price"
                      value={fmtMoney(email?.listedPrice)}
                    />
                    <Field
                      label="Payout / ticket"
                      value={fmtMoney(email?.payoutPerTicket)}
                    />
                    <Field
                      label="Total payout"
                      value={fmtMoney(email?.totalPayout)}
                    />
                  </dl>
                </div>
              </details>

              {email?.parseStatus === "parse_error" && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                  Parse error: {email.parseError}
                </div>
              )}

              <div className="rounded-md border border-border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-foreground">
                    Match
                  </h3>
                  <div className="flex items-center gap-2">
                    {match?.inventoryId != null && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={() => id != null && unlink.mutate({ id })}
                        disabled={unlink.isPending}
                      >
                        {unlink.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Unlink2 className="h-3 w-3 text-amber-400" />
                        )}
                        Unlink
                      </Button>
                    )}
                    {(
                      [
                        { mode: "parse", label: "Re-parse" },
                        { mode: "match", label: "Re-match" },
                        { mode: "both", label: "Both" },
                      ] as const
                    ).map(({ mode, label }) => {
                      const pending =
                        rematch.isPending &&
                        rematch.variables?.data?.mode === mode;
                      return (
                        <Button
                          key={mode}
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() =>
                            id != null && rematch.mutate({ id, data: { mode } })
                          }
                          disabled={rematch.isPending}
                        >
                          {pending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3 w-3 text-blue-400" />
                          )}
                          {label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                {match ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant={statusVariant(match.matchStatus)}>
                        {STATUS_LABELS[match.matchStatus] ?? match.matchStatus}
                      </Badge>
                      {match.inventoryId != null &&
                        (detail.matchReachListingId ? (
                          <Link
                            href={`/?focus=${encodeURIComponent(detail.matchReachListingId)}`}
                            className="text-primary hover:underline"
                            onClick={onClose}
                          >
                            Inventory #{match.inventoryId}
                          </Link>
                        ) : (
                          <span className="text-secondary-foreground">
                            Inventory #{match.inventoryId}
                          </span>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Field label="Qty sold" value={match.quantitySold} />
                      <Field
                        label="Qty remaining"
                        value={match.quantityRemaining}
                      />
                    </div>
                    {match.matchExplanation && (
                      <p className="text-xs text-secondary-foreground">
                        {match.matchExplanation}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-secondary-foreground">
                    No match record yet.
                  </p>
                )}
              </div>

              <div className="rounded-md border border-border p-3 space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-foreground">
                  Existing Reach invoice
                </h3>
                {invoice ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Field label="Invoice ID" value={invoice.reachInvoiceId} />
                    <Field label="Vendor" value={invoice.vendorEmail} />
                    <Field label="Qty sold" value={invoice.quantitySold} />
                    <Field
                      label="Net proceeds"
                      value={fmtMoney(invoice.totalNetProceeds)}
                    />
                    <Field
                      label="Sec / Row"
                      value={[invoice.section, invoice.row]
                        .filter(Boolean)
                        .join(" / ")}
                    />
                    <Field label="Seats" value={invoice.seats.join(", ") || "—"} />
                  </div>
                ) : (
                  <p className="text-xs text-secondary-foreground">
                    No existing Reach sale invoice for this listing. (Read-only —
                    no sale is created automatically.)
                  </p>
                )}
              </div>

              <div className="rounded-md border border-border p-3 space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-foreground">
                  Candidate inventory ({detail.candidates.length})
                </h3>
                {detail.candidates.length === 0 ? (
                  <p className="text-xs text-secondary-foreground">
                    No candidate inventory rows found.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {detail.candidates.map((c: SaleCandidate) => {
                      const isLinked = match?.inventoryId === c.id;
                      const candSeats =
                        c.seatsDisplay ??
                        [c.seatFrom, c.seatTo]
                          .filter(Boolean)
                          .join("–") ??
                        "";
                      const emailSeats = email?.seats.join(", ") || "";
                      return (
                        <div
                          key={c.id}
                          className={`rounded border px-3 py-2 ${
                            isLinked
                              ? "border-emerald-500/50 bg-emerald-500/5"
                              : "border-border/60"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <div className="min-w-0 truncate text-xs">
                              {c.reachListingId ? (
                                <Link
                                  href={`/?focus=${encodeURIComponent(c.reachListingId)}`}
                                  className="text-primary hover:underline font-medium"
                                  onClick={onClose}
                                >
                                  #{c.id}
                                </Link>
                              ) : (
                                <span className="font-medium">#{c.id}</span>
                              )}
                              <span className="text-secondary-foreground">
                                {" "}
                                · score {c.matchScore}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant={isLinked ? "secondary" : "outline"}
                              onClick={() =>
                                id != null &&
                                manualMatch.mutate({
                                  id,
                                  data: { inventoryId: c.id },
                                })
                              }
                              disabled={manualMatch.isPending}
                            >
                              {isLinked ? "Linked" : "Link"}
                            </Button>
                          </div>
                          <div className="grid grid-cols-[88px_1fr_1fr] gap-2 pb-1 text-[10px] uppercase tracking-wide text-secondary-foreground border-b border-border/40">
                            <span />
                            <span>From email</span>
                            <span>Candidate listing</span>
                          </div>
                          <CompareRow
                            label="Account"
                            emailValue={email?.recipientEmail}
                            candidateValue={c.vendorAccountEmail}
                            diff={
                              norm(email?.recipientEmail) !==
                              norm(c.vendorAccountEmail)
                            }
                          />
                          <CompareRow
                            label="Event"
                            emailValue={email?.eventName}
                            candidateValue={c.eventName}
                            diff={norm(email?.eventName) !== norm(c.eventName)}
                          />
                          <CompareRow
                            label="Event date"
                            emailValue={fmtEventDate(email?.eventDateText)}
                            candidateValue={fmtEventDate(c.eventDate)}
                          />
                          <CompareRow
                            label="Sec/Row/Seat"
                            emailValue={
                              <SeatBlock
                                section={email?.section}
                                row={email?.row}
                                seats={email?.seats}
                              />
                            }
                            candidateValue={
                              <SeatBlock
                                section={c.section}
                                row={c.row}
                                seats={candSeats ? [candSeats] : []}
                              />
                            }
                          />
                          <CompareRow
                            label="Quantity"
                            emailValue={email?.quantity}
                            candidateValue={c.availableQuantity}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() =>
                    id != null && review.mutate({ id, data: { reviewed: true } })
                  }
                  disabled={review.isPending}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  {match?.reviewedAt ? "Reviewed" : "Mark reviewed"}
                </Button>
              </div>

              <div className="rounded-md border border-border p-3 space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-foreground">
                  Ignore
                </h3>
                {isIgnored ? (
                  <div className="space-y-2">
                    <p className="text-xs text-secondary-foreground">
                      Ignored
                      {email?.ignoredBy ? ` by ${email.ignoredBy}` : ""}
                      {email?.ignoredAt ? ` · ${fmtDate(email.ignoredAt)}` : ""}
                      {email?.ignoreReason ? ` — ${email.ignoreReason}` : ""}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full rounded-full"
                      onClick={() =>
                        id != null && ignore.mutate({ id, data: { ignored: false } })
                      }
                      disabled={ignore.isPending}
                    >
                      <Eye className="h-3.5 w-3.5 text-emerald-500" />
                      Restore to queue
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-secondary-foreground">
                      Hide emails that don&apos;t match inventory we handle so
                      they drop out of the working queue.
                    </p>
                    <Input
                      placeholder="Reason (optional)"
                      value={ignoreReason}
                      onChange={(e) => setIgnoreReason(e.target.value)}
                      className="h-8"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full rounded-full"
                      onClick={() =>
                        id != null &&
                        ignore.mutate({
                          id,
                          data: {
                            ignored: true,
                            reason: ignoreReason || null,
                          },
                        })
                      }
                      disabled={ignore.isPending}
                    >
                      <EyeOff className="h-3.5 w-3.5 text-amber-400" />
                      Mark ignored
                    </Button>
                  </div>
                )}
              </div>

              {detail.rawBody && (
                <details className="rounded-md border border-border p-3">
                  <summary className="text-xs font-semibold uppercase tracking-wide text-secondary-foreground cursor-pointer">
                    Raw email
                  </summary>
                  <pre className="mt-2 text-[11px] whitespace-pre-wrap text-secondary-foreground max-h-64 overflow-auto">
                    {detail.rawBody}
                  </pre>
                </details>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
